import React, { useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js";

// Mesmo registro de linha dos gráficos irmãos (volume-bruto). Sem dependências novas.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export interface AccumulatedChartColors {
  realized: string;
  realizedFill: string;
  target: string;
  projected: string;
  todayLine: string;
  lastPoint: string;
  text: string;
  grid: string;
}

export interface AccumulatedChartProps {
  /** Datas cruas (yyyy-MM-dd) alinhadas às séries, para o tooltip. */
  rawDates: string[];
  /** Rótulos do eixo X já formatados (dd/MM). */
  labels: string[];
  /** Realizado acumulado; null nos dias só de projeção. */
  realized: Array<number | null>;
  /** Reta ideal de meta acumulada (ramp 0→meta mensal), um valor por dia. */
  target: number[];
  /** Acumulado projetado; null no trecho real (exceto o ponto de junção "hoje"). */
  projected: Array<number | null>;
  /** Índice do último dia real (divisor "hoje"); -1 quando não há série. */
  todayIndex: number;
  /** Fechamento projetado (rótulo no fim do traço projetado); null quando sem projeção. */
  projectedClose: number | null;
  colors: AccumulatedChartColors;
  locale: string;
  labelsText: {
    realized: string;
    target: string;
    projected: string;
    today: string;
    unit: string;
    closePrefix: string;
  };
  height?: number;
}

const formatNumber = (value: number, locale: string, decimals = 0): string =>
  Number(value).toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const AccumulatedChart: React.FC<AccumulatedChartProps> = ({
  rawDates,
  labels,
  realized,
  target,
  projected,
  todayIndex,
  projectedClose,
  colors,
  locale,
  labelsText,
  height = 340,
}) => {
  const lastProjectedIndex = useMemo(() => {
    for (let i = projected.length - 1; i >= 0; i -= 1) {
      if (projected[i] != null) return i;
    }
    return -1;
  }, [projected]);

  // react-chartjs-2 v5 injeta `plugins` só na criação do gráfico (nunca em update()), então
  // plugins inline congelam o closure do 1º render. Lemos os valores dinâmicos via refs para o
  // divisor "hoje" e o rótulo de fechamento refletirem os props atuais em cada draw.
  const todayIndexRef = useRef(todayIndex);
  const lastProjectedIndexRef = useRef(lastProjectedIndex);
  const projectedCloseRef = useRef(projectedClose);
  const todayLineColorRef = useRef(colors.todayLine);
  const textColorRef = useRef(colors.text);
  const projectedColorRef = useRef(colors.projected);
  const todayLabelRef = useRef(labelsText.today);
  const closePrefixRef = useRef(labelsText.closePrefix);
  const localeRef = useRef(locale);
  todayIndexRef.current = todayIndex;
  lastProjectedIndexRef.current = lastProjectedIndex;
  projectedCloseRef.current = projectedClose;
  todayLineColorRef.current = colors.todayLine;
  textColorRef.current = colors.text;
  projectedColorRef.current = colors.projected;
  todayLabelRef.current = labelsText.today;
  closePrefixRef.current = labelsText.closePrefix;
  localeRef.current = locale;

  const data = useMemo<ChartData<"line">>(
    () => ({
      labels,
      datasets: [
        {
          label: labelsText.target,
          data: target,
          borderColor: colors.target,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHitRadius: 0,
          fill: false,
          tension: 0,
          order: 3,
        },
        {
          label: labelsText.realized,
          data: realized,
          borderColor: colors.realized,
          backgroundColor: colors.realizedFill,
          borderWidth: 3,
          fill: "origin",
          tension: 0.25,
          spanGaps: false,
          pointRadius: realized.map((v, i) => (v == null ? 0 : i === todayIndex ? 5 : 0)),
          pointBackgroundColor: colors.lastPoint,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          order: 1,
        },
        {
          label: labelsText.projected,
          data: projected,
          borderColor: colors.projected,
          borderWidth: 3,
          borderDash: [6, 5],
          fill: false,
          tension: 0.25,
          spanGaps: true,
          pointRadius: projected.map((v, i) => (v != null && i === lastProjectedIndex ? 5 : 0)),
          pointBackgroundColor: colors.projected,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          order: 2,
        },
      ],
    }),
    [labels, realized, target, projected, todayIndex, lastProjectedIndex, colors, labelsText],
  );

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 54 } }, // espaço para o rótulo de fechamento
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: colors.lastPoint,
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: 600 },
          filter: (item) => item.raw !== null && item.raw !== undefined,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const raw = rawDates[idx];
              if (!raw) return "";
              const [y, m, d] = raw.split("-").map(Number);
              return new Date(y, m - 1, d).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
            },
            label: (item) => {
              const dec = item.datasetIndex === 0 ? 1 : 0;
              return `${item.dataset.label}: ${formatNumber(Number(item.raw), locale, dec)} ${labelsText.unit}`;
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: colors.text, maxRotation: 0, autoSkip: true, maxTicksLimit: 12, font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: colors.grid }, ticks: { color: colors.text, font: { size: 11 }, padding: 8 } },
      },
    }),
    [colors, locale, rawDates, labelsText],
  );

  // Divisor vertical "hoje".
  const todayDividerPlugin = useMemo<Plugin<"line">>(
    () => ({
      id: "accumToday",
      afterDatasetsDraw: (chart) => {
        const idx = todayIndexRef.current;
        if (idx < 0) return;
        const { ctx, chartArea, scales } = chart;
        const x = scales.x.getPixelForValue(idx);
        if (!Number.isFinite(x)) return;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([2, 3]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = todayLineColorRef.current;
        ctx.moveTo(x, chartArea.top + 12);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = textColorRef.current;
        ctx.font = "600 10px Montserrat, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(todayLabelRef.current, x, chartArea.top + 4);
        ctx.restore();
      },
    }),
    [],
  );

  // Rótulo de fechamento projetado no fim do traço projetado.
  const closeLabelPlugin = useMemo<Plugin<"line">>(
    () => ({
      id: "accumClose",
      afterDatasetsDraw: (chart) => {
        const idx = lastProjectedIndexRef.current;
        const close = projectedCloseRef.current;
        if (idx < 0 || close == null) return;
        const { ctx, scales } = chart;
        const x = scales.x.getPixelForValue(idx);
        const y = scales.y.getPixelForValue(close);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        const text = `${closePrefixRef.current} ${formatNumber(close, localeRef.current, 0)}`;
        ctx.save();
        ctx.font = "700 11px Montserrat, sans-serif";
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.fillStyle = projectedColorRef.current;
        ctx.fillText(text, x - 6, y - 10);
        ctx.restore();
      },
    }),
    [],
  );

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <Line data={data} options={options} plugins={[todayDividerPlugin, closeLabelPlugin]} />
    </div>
  );
};

export default AccumulatedChart;
